import { ImageUrl } from './image.service';

/**
 * Tries to locate the uploaded icon for a given info label by comparing the
 * decoded filename (without extension) to the normalized label.
 */
export function findImageUrlForLabel(
  infoLabel: string,
  images: ImageUrl[] | null | undefined,
  fallbackUrl: string
): string {
  const normalizedLabel = normalizeInfoLabel(infoLabel);
  if (!normalizedLabel || !images?.length) {
    return fallbackUrl;
  }

  const match = images.find(img => {
    const fileStem = extractFileStem(img.url);
    return fileStem === normalizedLabel;
  });

  return match ? match.url : fallbackUrl;
}

export function buildIconFileName(infoLabel: string): string {
  const normalizedLabel = normalizeInfoLabel(infoLabel);
  return `${normalizedLabel || 'icon'}.png`;
}

export function normalizeInfoLabel(infoLabel: string): string {
  return (infoLabel ?? '').trim().toLowerCase();
}

export function renameFileForInfoLabel(file: File, infoLabel: string): File {
  const fileName = buildIconFileName(infoLabel);
  if (typeof File === 'undefined') {
    return file;
  }
  try {
    return new File([file], fileName, { type: file.type || 'image/png' });
  } catch {
    return file;
  }
}

export function extractIconFileName(url: string | undefined): string {
  return extractFileName(url);
}

function extractFileStem(url: string | undefined): string {
  const fileName = extractFileName(url).toLowerCase();
  return fileName.endsWith('.png') ? fileName.slice(0, -4) : fileName;
}

function extractFileName(url: string | undefined): string {
  if (!url) {
    return '';
  }
  const withoutQuery = url.split('?')[0];
  const lastSlash = withoutQuery.lastIndexOf('/');
  const fileName = lastSlash >= 0 ? withoutQuery.slice(lastSlash + 1) : withoutQuery;
  return decodeSafe(fileName);
}

function decodeSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
